import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemAvatar,
  Avatar,
  TextField,
  InputAdornment,
  IconButton,
  Chip,
  CircularProgress,
  Alert,
  Divider,
  Badge,
  Tooltip,
} from '@mui/material';
import {
  Search as SearchIcon,
  Clear as ClearIcon,
  Person as PersonIcon,
  PriorityHigh as PriorityHighIcon,
  Lock as LockIcon,
  MailOutline as MailOutlineIcon,
  SmsOutlined as SmsOutlinedIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
  HelpOutline as HelpOutlineIcon,
  Login as LoginIcon,
} from '@mui/icons-material';
import { ChatRoom, ChatRoomFixedTag } from '../../../types';
import apiClient from '../../../utils/api';
import { useSearch } from '../../../hooks/useSearch';
import { useAdvancedSearch } from '../../../hooks/useAdvancedSearch';
import SearchFilters from './SearchFilters';
import { devLog } from '../../../utils/logger';

interface InboxSidebarProps {
  chats: ChatRoom[];
  selectedChatId: string | null;
  onChatSelect: (chatId: string) => void;
  loading?: boolean;
  error?: string | null;
  onRefresh?: () => void;
  selectedCategory?: string; // カテゴリーフィルター用
}

export const InboxSidebar: React.FC<InboxSidebarProps> = ({
  chats,
  selectedChatId,
  onChatSelect,
  loading = false,
  error = null,
  onRefresh,
  selectedCategory = 'inbox',
}) => {
  const [useAdvancedMode, setUseAdvancedMode] = useState(false);
  const [fixedTagMap, setFixedTagMap] = useState<Record<string, ChatRoomFixedTag[]>>({});
  const [fixedTagLoaded, setFixedTagLoaded] = useState(false);

  // ソート: 'desc'=新しい順（デフォルト）, 'asc'=古い順
  const [sortOrder, setSortOrder] = useState<'desc' | 'asc'>('desc');

  // 固定タグフィルタリング用データ読み込み（一括API）
  useEffect(() => {
    if (!selectedCategory?.startsWith('fixedtag:')) {
      setFixedTagLoaded(false);
      return;
    }
    const chatIds = chats.map(c => c.id);
    if (chatIds.length === 0) return;

    setFixedTagLoaded(false);
    let cancelled = false;
    (async () => {
      try {
        const res = await apiClient.getBulkRoomFixedTags(chatIds);
        if (cancelled) return;
        if (res.success && res.map) {
          setFixedTagMap(res.map);
          setFixedTagLoaded(true);
        }
      } catch (e) {
        devLog('固定タグ一括取得エラー:', e);
        if (!cancelled) setFixedTagLoaded(true);
      }
    })();
    return () => { cancelled = true; };
  }, [selectedCategory, chats]);


  // 基本検索機能
  const {
    searchTerm,
    activeFilters,
    filteredResults,
    resultCount,
    loading: searchLoading,
    hasActiveFilters,
    setSearchTerm,
    clearSearch,
    setFilter,
    clearFilter,
    clearAllFilters,
    toggleFilter,
    applyQuickFilter,
  } = useSearch({
    data: chats,
    searchFields: ['title', 'last_message', 'participant_name', 'participant_phone', 'participant_email', 'notes', 'project_no', 'room_number'],
    debounceMs: 300,
  });

  // 拡張検索機能
  const {
    searchResults,
    filters: advancedFilters,
    updateFilters: updateAdvancedFilters,
    resetFilters: resetAdvancedFilters,
    searchStats,
    isSearching,
    highlightText,
  } = useAdvancedSearch({
    data: chats,
    debounceMs: 300,
    enableHighlighting: true,
  });

  // 利用可能なタグと運営者の取得
  const availableTags = Array.from(new Set(chats.flatMap(chat => chat.tags || [])));
  const availableOperators = Array.from(new Set(chats.map(chat => ({
    id: chat.created_by,
    name: chat.created_by, // 実際にはユーザー名に置き換える
  })).filter(op => op.id)));

  // 検索モード切り替え
  const toggleSearchMode = () => {
    setUseAdvancedMode(prev => !prev);
    if (useAdvancedMode) {
      resetAdvancedFilters();
    } else {
      setSearchTerm('');
      clearAllFilters();
    }
  };

  // カテゴリーフィルタリング（新しいカテゴリに対応）
  const filterChatsByCategory = (chats: ChatRoom[], category: string): ChatRoom[] => {
    devLog('🔍 カテゴリーフィルタリング:', { category, totalChats: chats.length });
    
    switch (category) {
      case 'all':
        return chats;
      case 'pending':
        // 未対応のチャットルーム
        return chats.filter(chat => chat.status === 'pending');
      case 'unassigned':
        // ステータス未設定のチャットルーム
        return chats.filter(chat => !chat.status);
      case 'active':
        return chats.filter(chat => chat.status === 'active');
      case 'waiting':
        return chats.filter(chat => chat.status === 'waiting');
      case 'completed':
        return chats.filter(chat => chat.status === 'completed');
      case 'temp_completed':
        return chats.filter(chat => chat.status === 'temp_completed');
      case 'paused':
        return chats.filter(chat => chat.status === 'paused');
      case 'uncompleted':
        // 削除：deleted と archived の両方を含む
        return chats.filter(chat => chat.status === 'archived');
      
      // 旧カテゴリ（互換性のため）
      case 'inbox':
        return chats.filter(chat => chat.status === 'active');
      case 'priority':
        return chats.filter(chat => 
          chat.priority === 'high' || chat.tags?.includes('priority') || chat.tags?.includes('urgent')
        );
      case 'starred':
        return chats.filter(chat => chat.tags?.includes('starred'));
      case 'snoozed':
        return chats.filter(chat => chat.tags?.includes('snoozed'));
      case 'archived':
        // archived カテゴリも deleted と archived の両方を含む
        return chats.filter(chat => chat.status === 'archived');
      default:
        // プレフィックス形式の動的フィルタ
        if (category?.startsWith('operator:')) {
          const operatorId = category.split(':')[1];
          devLog('👤 運営者フィルタリング:', { operatorId, totalChats: chats.length });
          
          const filtered = chats.filter(chat => {
            const createdBy = chat.created_by;
            const createdByName = chat.created_by_name;
            const assignedId = (chat as any).assigned_operator_id;
            
            // name:形式の場合は表示名で比較
            if (operatorId?.startsWith('name:')) {
              const displayName = operatorId.slice(5);
              const match = createdByName === displayName;
              devLog('  名前比較:', { displayName, createdByName, match });
              return match;
            }
            
            // IDで比較（文字列として）
            const match = createdBy === operatorId || assignedId === operatorId;
            devLog('  ID比較:', { createdBy, assignedId, operatorId, match });
            return match;
          });
          
          devLog('👤 運営者フィルター結果:', { filteredCount: filtered.length });
          return filtered;
        }
        
        if (category?.startsWith('fixedtag:')) {
          const optionId = category.split(':')[1];
          devLog('🏷️ 固定タグフィルタリング:', { optionId, totalChats: chats.length, fixedTagLoaded });
          
          if (!fixedTagLoaded) {
            return [];
          }
          
          const filtered = chats.filter(chat => {
            const tags = fixedTagMap[chat.id] || [];
            return tags.some(t => t.option_id === optionId);
          });
          
          devLog('🏷️ 固定タグフィルター結果:', { filteredCount: filtered.length });
          return filtered;
        }

        if (category?.startsWith('tag:') || category?.startsWith('tag-')) {
          const tagId = category.includes(':') ? category.split(':')[1] : category.replace('tag-', '');
          devLog('🏷️ タグフィルタリング:', { tagId, totalChats: chats.length });
          
          const filtered = chats.filter(chat => {
            const customTags = chat.custom_tags || [];
            const tags = (chat as any).tags || [];
            const match = customTags.includes(tagId) || tags.includes(tagId);
            
            if (match) {
              devLog('  タグマッチ:', { chatId: chat.id, customTags, tags, tagId });
            }
            
            return match;
          });
          
          devLog('🏷️ タグフィルター結果:', { filteredCount: filtered.length });
          return filtered;
        }
        return chats;
    }
  };

  // 表示するチャットデータを決定
  const filteredChats = useAdvancedMode 
    ? filterChatsByCategory(searchResults.map(result => result.item), selectedCategory)
    : filterChatsByCategory(filteredResults, selectedCategory);

  // 最終アクション日時でソート
  const displayChats = React.useMemo(() => {
    const sorted = [...filteredChats].sort((a, b) => {
      const timeA = new Date(a.updated_at || a.created_at).getTime();
      const timeB = new Date(b.updated_at || b.created_at).getTime();
      return sortOrder === 'desc' ? timeB - timeA : timeA - timeB;
    });
    return sorted;
  }, [filteredChats, sortOrder]);

  devLog('🎯 InboxSidebar フィルタリング状況:', {
    selectedCategory,
    totalChats: chats.length,
    filteredResultsCount: filteredResults.length,
    displayChatsCount: displayChats.length,
    chatStatuses: chats.map(c => ({ id: c.id, status: c.status, title: c.title }))
  });

  // 結果数とローディング状態
  const finalResultCount = useAdvancedMode ? searchStats.totalResults : resultCount;
  const finalIsLoading = useAdvancedMode ? isSearching : searchLoading;

  // 未読数を取得してバッジ表示を更新（同一結果ではstate更新しない）
  const [unreadMap, setUnreadMap] = React.useState<Record<string, number>>({});
  const displayIdsKey = React.useMemo(() => displayChats.map(c => c.id).join(','), [displayChats]);
  React.useEffect(() => {
    let cancelled = false;

    const setIfChanged = (next: Record<string, number>) => {
      setUnreadMap((prev) => {
        const prevKeys = Object.keys(prev);
        const nextKeys = Object.keys(next);
        if (prevKeys.length !== nextKeys.length) return next;
        for (const k of nextKeys) {
          if (prev[k] !== next[k]) return next;
        }
        return prev;
      });
    };

    const ids = displayIdsKey ? displayIdsKey.split(',').filter(Boolean) : [];
    if (ids.length === 0) {
      setUnreadMap((prev) => (Object.keys(prev).length === 0 ? prev : {}));
      return () => { cancelled = true; };
    }

    const fetchUnread = async () => {
      try {
        const res = await apiClient.getBatchUnreadCounts(ids);
        if (!cancelled && res.success) {
          setIfChanged(res.counts);
        }
      } catch {}
    };
    fetchUnread();
    return () => { cancelled = true; };
  }, [displayIdsKey]);

  // 優先度に応じた色を取得
  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high': return 'error';
      case 'medium': return 'warning';
      case 'low': return 'success';
      default: return 'default';
    }
  };

  // チャットの最終メッセージ時間をフォーマット
  const formatLastMessageTime = (dateString: string) => {
    if (!dateString) return '—';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '—';
    const now = new Date();
    const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60));
    
    if (diffInMinutes < 1) return 'たった今';
    if (diffInMinutes < 60) return `${diffInMinutes}分前`;
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}時間前`;
    return `${Math.floor(diffInMinutes / 1440)}日前`;
  };

  return (
    <Box sx={{ 
      height: '100%', 
      display: 'flex', 
      flexDirection: 'column',
      backgroundColor: 'background.paper',
    }}>
      {/* ヘッダーと検索バー */}
      <Box sx={{ p: 2, border: 'none' }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, fontSize: '0.95rem' }}>
          チャット一覧 ({displayChats.length})
        </Typography>
        
        {useAdvancedMode ? (
          <SearchFilters
            filters={advancedFilters}
            onFiltersChange={updateAdvancedFilters}
            onSearch={(term) => updateAdvancedFilters({ searchTerm: term })}
            availableTags={availableTags}
            availableOperators={availableOperators}
            compactMode={false}
          />
        ) : (
          <TextField
            fullWidth
            placeholder="チャットを検索..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  {(searchTerm || hasActiveFilters) && (
                    <IconButton 
                      size="small" 
                      onClick={() => {
                        clearSearch();
                        clearAllFilters();
                      }}
                    >
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  )}
                </InputAdornment>
              ),
            }}
          />
        )}
        
        {/* ソートボタン + 検索ヘルプ */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 0.5 }}>
          <Tooltip title="経過時間でソート" arrow>
            <IconButton
              size="small"
              onClick={() => setSortOrder(prev => prev === 'desc' ? 'asc' : 'desc')}
              sx={{
                fontSize: '0.75rem',
                borderRadius: 1,
                px: 1,
                py: 0.25,
                border: '1px solid',
                borderColor: 'divider',
                '&:hover': { bgcolor: 'action.hover' },
              }}
            >
              {sortOrder === 'desc' ? (
                <ArrowDownwardIcon sx={{ fontSize: 14, mr: 0.5 }} />
              ) : (
                <ArrowUpwardIcon sx={{ fontSize: 14, mr: 0.5 }} />
              )}
              <Typography variant="caption" sx={{ fontSize: '0.7rem', lineHeight: 1 }}>
                {sortOrder === 'desc' ? '新しい順' : '古い順'}
              </Typography>
            </IconButton>
          </Tooltip>
          <Tooltip
            title={
              <Box sx={{ fontSize: '0.75rem', lineHeight: 1.6 }}>
                <Typography variant="caption" sx={{ fontWeight: 'bold', display: 'block', mb: 0.5 }}>検索対象:</Typography>
                ・ルーム名<br />
                ・参加者名<br />
                ・電話番号<br />
                ・メールアドレス<br />
                ・案件No.<br />
                ・ルームNo.<br />
                ・メモ<br />
                ・最終メッセージ
              </Box>
            }
            arrow
            placement="bottom-end"
          >
            <IconButton size="small" sx={{ p: 0.5 }}>
              <HelpOutlineIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
            </IconButton>
          </Tooltip>
        </Box>

        {/* 検索結果表示 */}
        <Box sx={{ px: 0, py: 0.5 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            {(searchTerm || hasActiveFilters || useAdvancedMode) && (
              <Typography variant="caption" color="text.secondary">
                {finalResultCount} 件の結果
                {finalIsLoading && <CircularProgress size={12} sx={{ ml: 1 }} />}
              </Typography>
            )}
          </Box>
        </Box>
      </Box>

      {/* エラー表示 */}
      {error && (
        <Alert severity="error" sx={{ m: 2 }}>
          {error}
          {onRefresh && (
            <IconButton size="small" onClick={onRefresh} sx={{ ml: 1 }}>
              <ClearIcon />
            </IconButton>
          )}
        </Alert>
      )}

      {/* チャット一覧 */}
      <Box sx={{ flex: 1, overflow: 'auto' }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
            <CircularProgress />
          </Box>
        ) : displayChats.length === 0 ? (
          <Box sx={{ p: 2, textAlign: 'center' }}>
            <Typography color="text.secondary">
              {searchTerm ? '検索結果が見つかりません' : 'チャットがありません'}
            </Typography>
          </Box>
        ) : (
          <List sx={{ p: 0 }}>
            {displayChats.map((chat) => (
              <React.Fragment key={chat.id}>
                <ListItem disablePadding>
                  <ListItemButton
                    selected={selectedChatId === chat.id}
                    onClick={() => {
                      // ロック中でも選択可能（閲覧のみ）
                      onChatSelect(chat.id);
                    }}
                    sx={{
                      px: 2,
                      py: 1.5,
                      '&.Mui-selected': {
                        backgroundColor: 'primary.light',
                        '&:hover': {
                          backgroundColor: 'primary.main',
                        },
                      },
                      // ルームがロック中の場合は背景色と半透明で視覚的に区別
                      ...(chat.locked_by && chat.locked_by !== (window as any).CSCHAT_SOCKET_ID ? {
                        backgroundColor: 'rgba(255, 152, 0, 0.08)', // オレンジ系の薄い背景
                        borderLeft: '4px solid #ff9800', // 左側に太いオレンジのボーダー
                        opacity: 0.85,
                        '&:hover': {
                          backgroundColor: 'rgba(255, 152, 0, 0.15)',
                        },
                        '&.Mui-selected': {
                          backgroundColor: 'rgba(255, 152, 0, 0.25)',
                          '&:hover': {
                            backgroundColor: 'rgba(255, 152, 0, 0.35)',
                          },
                        },
                      } : {}),
                    }}
                  >
                    <ListItemAvatar>
                      <Box sx={{ position: 'relative', display: 'inline-block' }}>
                        <Badge
                          badgeContent={unreadMap[chat.id] || 0}
                          color="error"
                          invisible={!((unreadMap[chat.id] || 0) > 0)}
                        >
                          <Avatar sx={{ width: 40, height: 40 }}>
                            {chat.participant_name?.charAt(0) || <PersonIcon />}
                          </Avatar>
                        </Badge>
                        {/* ロック中の場合、アバター左下にロックアイコンを表示 */}
                        {chat.locked_by && chat.locked_by !== (window as any).CSCHAT_SOCKET_ID && (
                          <Box
                            sx={{
                              position: 'absolute',
                              bottom: -2,
                              left: -2,
                              width: 18,
                              height: 18,
                              borderRadius: '50%',
                              backgroundColor: '#ff9800',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              border: '2px solid white',
                              boxShadow: 1,
                            }}
                          >
                            <LockIcon sx={{ fontSize: 10, color: 'white' }} />
                          </Box>
                        )}
                      </Box>
                    </ListItemAvatar>
                    
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: (chat.unread_count || 0) > 0 ? 600 : 500,
                              fontSize: '0.8125rem',
                              flex: 1,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {chat.room_number ? `#${chat.room_number} ` : ''}{chat.participant_name || 'Unknown User'}
                          </Typography>
                          {chat.priority === 'high' && (
                            <PriorityHighIcon sx={{ fontSize: 16, color: 'error.main' }} />
                          )}
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                            {formatLastMessageTime(chat.updated_at)}
                          </Typography>
                        </Box>
                      }
                      secondary={
                        <Box sx={{ mt: 0.5 }}>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{
                              fontSize: '0.75rem',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              mb: 0.5,
                              lineHeight: 1.3,
                            }}
                          >
                            {chat.last_message || '新しいチャットルーム'}
                          </Typography>

                          {/* E/Uログインバッジ */}
                          <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', mb: 0.5 }}>
                            {/* 送信経路 */}
                            {chat.participant_email && (
                              <MailOutlineIcon sx={{ fontSize: 13, color: 'text.disabled' }} />
                            )}
                            {chat.participant_phone && (
                              <SmsOutlinedIcon sx={{ fontSize: 13, color: 'text.disabled' }} />
                            )}
                            {/* ログイン済み / 未ログイン */}
                            <Chip
                              icon={<LoginIcon sx={{ fontSize: '10px !important' }} />}
                              label={chat.eu_logged_in ? 'ログイン済' : '未ログイン'}
                              size="small"
                              sx={{
                                height: 18, fontSize: '0.6rem', fontWeight: 600,
                                bgcolor: chat.eu_logged_in ? 'success.main' : 'action.disabledBackground',
                                color: chat.eu_logged_in ? '#fff' : 'text.secondary',
                                '& .MuiChip-icon': { color: chat.eu_logged_in ? '#fff' : 'text.secondary', ml: '3px' },
                              }}
                            />
                          </Box>
                          
                          {/* タグ表示 */}
                          {chat.tags && chat.tags.length > 0 && (
                            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                              {chat.tags.slice(0, 2).map((tag) => (
                                <Chip
                                  key={tag}
                                  label={tag}
                                  size="small"
                                  sx={{ height: 20, fontSize: '0.7rem' }}
                                />
                              ))}
                              {chat.tags.length > 2 && (
                                <Typography variant="caption" color="text.secondary">
                                  +{chat.tags.length - 2}
                                </Typography>
                              )}
                            </Box>
                          )}
                        </Box>
                      }
                    />
                  </ListItemButton>
                </ListItem>
                <Divider variant="inset" component="li" />
              </React.Fragment>
            ))}
          </List>
        )}
      </Box>
    </Box>
  );
};